import type React from "react";
import { useCallback, useState } from "react";
import type { NurseInShift } from "../../../../../../utils/models";

export type SelectedCreationType = "single" | "recurring" | null;

export const useDayCellState = ({ onCreatedSingleShift, interactionsDisabled }: { onCreatedSingleShift?: () => void; interactionsDisabled?: boolean }) => {
	const [popoverOpen, setPopoverOpen] = useState(false);
	const [nurseMenuAnchorAndSpecificNurse, setNurseMenuAnchorAndSpecificNurse] = useState<null | {
		element: HTMLElement;
		specificNurseInShift: NurseInShift;
	}>(null);
	const [selectedCreationType, setSelectedCreationType] = useState<SelectedCreationType>(null);
	// Anchor + shift id for the actual-shift job-edit popover (DAT-502). Multi-job employees can
	// reassign an actual shift's job straight from the profile grid.
	const [editingActualJob, setEditingActualJob] = useState<null | { id: string; element: HTMLElement }>(null);
	// The single-job employee's job name, held while the hint dialog is open (DAT-615). No anchor and
	// no shift id: it's a modal, and it only explains the flow rather than acting on a shift.
	const [singleJobHint, setSingleJobHint] = useState<null | { jobName: string }>(null);

	const togglePopover = useCallback(() => {
		if (interactionsDisabled) {
			return;
		}
		setSelectedCreationType(null);
		setPopoverOpen((prev) => !prev);
	}, [interactionsDisabled]);

	const handleCellClick = useCallback(
		(event: React.MouseEvent<HTMLDivElement>, specificNurseInShift?: NurseInShift) => {
			if (interactionsDisabled) {
				return;
			}
			if (specificNurseInShift) {
				// Don't let the cell click also trigger when clicking on a specific nurse in shift
				event.stopPropagation();

				setNurseMenuAnchorAndSpecificNurse({
					element: event.currentTarget,
					specificNurseInShift,
				});
			} else {
				togglePopover();
			}
		},
		[togglePopover, interactionsDisabled],
	);

	const closeNurseMenu = useCallback(() => {
		setNurseMenuAnchorAndSpecificNurse(null);
	}, []);

	const onPopoverClickOutside = useCallback(() => {
		if (!selectedCreationType) {
			togglePopover();
		}
	}, [selectedCreationType, togglePopover]);

	const handleClickedCellEditorClose = useCallback(
		(createdSingleShift: boolean) => {
			togglePopover();
			if (createdSingleShift && onCreatedSingleShift) {
				onCreatedSingleShift();
			}
		},
		[togglePopover, onCreatedSingleShift],
	);

	return {
		nurseMenuAnchorAndSpecificNurse,
		handleCellClick,
		popoverOpen,
		closeNurseMenu,
		selectedCreationType,
		setSelectedCreationType,
		onPopoverClickOutside,
		handleClickedCellEditorClose,
		editingActualJob,
		setEditingActualJob,
		singleJobHint,
		setSingleJobHint,
	};
};
